import { useState } from "react";
import { sentence, video, word } from "shared/types/Report";
import useFirestore from "shared/hooks/useFirestore";

type AllowedTypes = word | sentence | video;

export const parseBatchLines = (value: string): string[] => {
    const lines = value.split(/\r?\n/).map((line) => line.trim());
    while (lines.length > 0 && lines[lines.length - 1] === "") {
        lines.pop();
    }
    return lines;
};

const sanitizeForFirestore = (data: any[]) => {
    return data.map((item) => {
        const cleaned: Record<string, unknown> = {};
        Object.entries(item).forEach(([key, value]) => {
            if (value === undefined || value === null) {
                cleaned[key] = "";
            } else if (typeof File !== "undefined" && value instanceof File) {
                cleaned[key] = "";
            } else {
                cleaned[key] = value;
            }
        });
        return cleaned;
    });
};

const useBatchView = <T extends AllowedTypes>(list: T[], setList: React.Dispatch<React.SetStateAction<T[]>>, path:string) => {
    const [progress, setProgress] = useState<number>(0);
    const [showProgress, setShowProgress] = useState<boolean>(false);
    const [uploadFileName, setUploadFileName] = useState<string>("");
    const {writeFirestore, deleteFirestore} = useFirestore();
    const [isOpenErrorModal, setIsOpenErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState("모든 콘텐츠의 수는 같아야 합니다");
    const [isSaveSucceed, setIsSaveSucceed] = useState(false);
    const onRegisterReportClick = async(type:string, data:any) => {
        if(!data.length){
            await deleteFirestore(path + "/Content", type).then(res => {
                if(res){
                    setIsSaveSucceed(true);
                } else {
                    setErrorMessage("저장에 실패하였습니다. 다시 시도해 주세요");
                    setIsOpenErrorModal(true);
                }
            })
            return;
        }
        const sanitized = sanitizeForFirestore(data);
        await writeFirestore(path + "/Content", type, {content:sanitized}).then(res => {
            if(res){
                setIsSaveSucceed(true);
            } else {
                setErrorMessage("저장에 실패하였습니다. 다시 시도해 주세요");
                setIsOpenErrorModal(true);
            }
        })
    }

    const onSuccessModalConfirmHandler = () => {
        setIsSaveSucceed(false);
    }
    const removeKeyFromObjects = <T extends AllowedTypes, K extends keyof T>(
        array: T[], 
        keyToRemove: K
    ): Omit<T, K>[] => {
        return array.map(obj => {
            const { [keyToRemove]: _, ...rest } = obj;
            return rest as Omit<T, K>;
        });
    };
    
    const onErrorModalCloseClickHandler = () => {
        setIsOpenErrorModal(false);
        setErrorMessage("모든 콘텐츠의 수는 같아야 합니다");
    }

    const openCountMismatchError = () => {
        setErrorMessage("모든 콘텐츠의 수는 같아야 합니다");
        setIsOpenErrorModal(true);
    }

    const openSaveFailedError = () => {
        setErrorMessage("저장에 실패하였습니다. 다시 시도해 주세요");
        setIsOpenErrorModal(true);
    }

    return{progress, setProgress, showProgress, setShowProgress, isSaveSucceed, onSuccessModalConfirmHandler, onRegisterReportClick, removeKeyFromObjects, uploadFileName, setUploadFileName, isOpenErrorModal, setIsOpenErrorModal, onErrorModalCloseClickHandler, errorMessage, openCountMismatchError, openSaveFailedError}
}

export default useBatchView;